import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { INSTAGRAM_URL } from "../assets";

export default function Navigation({ lang, setLang, t }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "work", label: t.nav.work },
    { id: "services", label: t.nav.services },
    { id: "agenda", label: t.nav.agenda },
    { id: "about", label: t.nav.about },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ink/85 backdrop-blur-md border-b border-moss/40" : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-12 lg:px-20 h-16 md:h-20">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <img
            src="/fl-logo.png"
            alt="Fight-Lens"
            className="h-9 w-9 md:h-10 md:w-10 rounded-full group-hover:scale-105 transition-transform duration-300"
          />
          <span translate="no" className="notranslate hidden sm:inline text-[10px] tracking-[0.3em] uppercase text-ash group-hover:text-bone transition-colors">FIGHT-LENS</span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="link-underline text-[11px] tracking-[0.32em] uppercase text-bone/85 hover:text-bone transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/tarifs"
            data-testid="nav-shop"
            className="link-underline text-[11px] tracking-[0.32em] uppercase text-leaf hover:text-bone transition-colors"
          >
            {t.nav.shop}
          </Link>
          <Link
            to="/commander"
            data-testid="nav-order"
            className="link-underline text-[11px] tracking-[0.32em] uppercase text-bone/85 hover:text-bone transition-colors"
          >
            {t.nav.order}
          </Link>
          <Link
            to="/chasse"
            data-testid="nav-hunt"
            className="link-underline text-[11px] tracking-[0.32em] uppercase text-bone/85 hover:text-bone transition-colors"
          >
            {t.nav.hunt}
          </Link>
        </nav>

        <div className="flex items-center gap-2 md:gap-5">
          <a
            data-testid="nav-instagram"
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="text-bone/80 hover:text-bone transition-colors p-3 -m-1"
          >
            <Instagram size={18} strokeWidth={1.5} />
          </a>
          <button
            data-testid="nav-lang-toggle"
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className="text-[11px] tracking-[0.32em] uppercase text-bone/80 hover:text-bone border border-line hover:border-leaf px-3.5 py-2.5 transition-colors"
          >
            {t.nav.lang}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            aria-expanded={open}
            className="md:hidden flex flex-col justify-center gap-1.5 p-3 -mr-2 min-h-[44px] min-w-[44px]"
          >
            <span className={`block h-px w-6 bg-bone transition-transform ${open ? "translate-y-[7px] rotate-45" : ""}`}></span>
            <span className={`block h-px w-6 bg-bone transition-opacity ${open ? "opacity-0" : ""}`}></span>
            <span className={`block h-px w-6 bg-bone transition-transform ${open ? "-translate-y-[7px] -rotate-45" : ""}`}></span>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu"
        className={`md:hidden overflow-hidden transition-[max-height] duration-500 bg-ink border-b border-moss/40 ${
          open ? "max-h-[calc(100svh-4rem)] overflow-y-auto" : "max-h-0"
        }`}
      >
        <div className="flex flex-col px-6 py-5 gap-1">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left font-serif text-3xl text-bone hover:text-leaf transition-colors py-2"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/tarifs"
            data-testid="mobile-nav-shop"
            onClick={() => setOpen(false)}
            className="text-left font-serif text-3xl text-bone hover:text-leaf transition-colors py-2 flex items-center gap-3"
          >
            {t.nav.shop}
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-leaf"></span>
          </Link>
          <Link
            to="/commander"
            data-testid="mobile-nav-order"
            onClick={() => setOpen(false)}
            className="text-left font-serif text-3xl text-bone hover:text-leaf transition-colors py-2"
          >
            {t.nav.order}
          </Link>
          <Link
            to="/chasse"
            data-testid="mobile-nav-hunt"
            onClick={() => setOpen(false)}
            className="text-left font-serif text-3xl text-bone hover:text-leaf transition-colors py-2"
          >
            {t.nav.hunt}
          </Link>
        </div>
      </div>
    </header>
  );
}
